/**
 * AdminHelpTab - painel de suporte do chat de dúvidas: Conversas (números, temas, telas e lista),
 * FAQ e avisos, Configurações. A conversa aberta vive na URL (?conversation=<id>), que é o link
 * do alerta do Telegram: abrir a URL abre o painel lateral direto, e Voltar o fecha.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn, safeLocaleString } from '@/lib/utils';
import AdminHelpConversation from '@/components/admin/help/AdminHelpConversation';
import AdminHelpConversationList from '@/components/admin/help/AdminHelpConversationList';
import AdminHelpLoadError from '@/components/admin/help/AdminHelpLoadError';
import AdminHelpNotes from '@/components/admin/help/AdminHelpNotes';
import AdminHelpSettings from '@/components/admin/help/AdminHelpSettings';
import {
  getHelpConversations,
  getHelpSummary,
  helpErrorMessage,
  type HelpFilters,
  type HelpListPage,
  type HelpPeriod,
  type HelpStatusFilter,
  type HelpSummary,
} from '@/services/adminHelpService';

type HelpView = 'conversations' | 'notes' | 'settings';

const VIEWS: { id: HelpView; label: string }[] = [
  { id: 'conversations', label: 'Conversas' },
  { id: 'notes', label: 'FAQ e avisos' },
  { id: 'settings', label: 'Configurações' },
];

const PERIODS: { value: HelpPeriod; label: string }[] = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

const STATUS_OPTIONS: { value: HelpStatusFilter; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'unknown', label: 'Não soube' },
  { value: 'needs_team', label: 'Precisou da equipe' },
  { value: 'thumbs_down', label: '👎' },
];

type ListQuery = { period: HelpPeriod; filters: HelpFilters; page: number };

const INITIAL_QUERY: ListQuery = { period: '30d', filters: { plan: '', status: '', q: '' }, page: 1 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// O plano se aplica ao digitar; a espera junta as teclas numa busca só.
const PLAN_DEBOUNCE_MS = 300;
const FIELD_CLASS = 'px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground';

// Centavos de dólar pedem 4 casas; de US$ 1 para cima, 2 bastam e o valor cabe no cartão a 375 px.
const formatCost = (usd: number) =>
  `US$ ${safeLocaleString(usd, 'pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: usd < 1 ? 4 : 2 })}`;

const AdminHelpTab: React.FC = () => {
  const [view, setView] = useState<HelpView>('conversations');
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationParam = searchParams.get('conversation');
  const openId = conversationParam && UUID_RE.test(conversationParam) ? conversationParam : null;

  // Período, filtros e página mudam juntos (filtro novo volta à página 1) para disparar uma busca só.
  const [query, setQuery] = useState<ListQuery>(INITIAL_QUERY);
  const { period, filters, page } = query;
  const [planInput, setPlanInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [summary, setSummary] = useState<HelpSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [list, setList] = useState<HelpListPage | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const applyFilters = useCallback((patch: Partial<HelpFilters>) => {
    setQuery((prev) => {
      const next = { ...prev.filters, ...patch };
      const same = next.plan === prev.filters.plan && next.status === prev.filters.status && next.q === prev.filters.q;
      return same ? prev : { ...prev, filters: next, page: 1 };
    });
  }, []);

  const toggleStatus = (status: HelpStatusFilter) => applyFilters({ status: filters.status === status ? '' : status });
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const timer = setTimeout(() => applyFilters({ plan: planInput.trim() }), PLAN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [planInput, applyFilters]);

  useEffect(() => {
    let stale = false;
    setSummaryLoading(true);
    setSummaryError(null);
    getHelpSummary(period, filters)
      .then((data) => {
        if (!stale) setSummary(data);
      })
      .catch((err) => {
        if (!stale) setSummaryError(helpErrorMessage(err));
      })
      .finally(() => {
        if (!stale) setSummaryLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [period, filters, reloadKey]);

  useEffect(() => {
    let stale = false;
    setListLoading(true);
    setListError(null);
    getHelpConversations(period, filters, page)
      .then((data) => {
        if (!stale) setList(data);
      })
      .catch((err) => {
        if (!stale) setListError(helpErrorMessage(err));
      })
      .finally(() => {
        if (!stale) setListLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [period, filters, page, reloadKey]);

  // Abrir empilha no histórico (Voltar fecha); fechar substitui, para Voltar não reabrir.
  const openConversation = (id: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('conversation', id);
      return next;
    });

  const closeConversation = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('conversation');
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const rated = summary ? summary.thumbs_up + summary.thumbs_down : 0;
  const thumbsUpRate = summary && rated > 0 ? `${Math.round((summary.thumbs_up / rated) * 100)}%` : '—';

  return (
    <div className="space-y-6">
      <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setView(v.id)}
            aria-pressed={view === v.id}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              view === v.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'notes' && <AdminHelpNotes />}
      {view === 'settings' && <AdminHelpSettings />}

      {view === 'conversations' && (
        <>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setQuery((prev) => (prev.period === p.value ? prev : { ...prev, period: p.value, page: 1 }))}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md transition-colors',
                  period === p.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={planInput}
              onChange={(e) => setPlanInput(e.target.value)}
              placeholder="Plano (trial, free, pro…)"
              aria-label="Plano"
              maxLength={200}
              className={cn(FIELD_CLASS, 'w-full sm:w-52')}
            />
            <select
              value={filters.status}
              onChange={(e) => applyFilters({ status: e.target.value as HelpStatusFilter })}
              aria-label="Situação"
              className={cn(FIELD_CLASS, 'w-full sm:w-auto')}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                applyFilters({ q: searchInput.trim() });
              }}
              className="flex w-full min-w-0 gap-2 sm:w-auto sm:flex-1"
            >
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="E-mail, nome ou texto da conversa"
                aria-label="Buscar"
                maxLength={200}
                className={cn(FIELD_CLASS, 'min-w-0 flex-1')}
              />
              <button
                type="submit"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Search className="size-4" aria-hidden />
                Buscar
              </button>
            </form>
          </div>

          {summaryError ? (
            <AdminHelpLoadError message={summaryError} onRetry={reload} />
          ) : !summary ? (
            <div className="py-8 text-center text-muted-foreground">Carregando números...</div>
          ) : (
            <div className={cn('space-y-6 transition-opacity', summaryLoading && 'opacity-60')}>
              {/* No máximo 4 por linha: com a barra lateral aberta, 8 colunas não cabem "US$ 0,0090" nem no maior container. */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label="Conversas" value={summary.conversations} />
                <StatCard label="Perguntas" value={summary.questions} />
                <StatCard label="Pessoas" value={summary.people} />
                <StatCard
                  label="Não soube"
                  value={summary.unknown}
                  active={filters.status === 'unknown'}
                  onClick={() => toggleStatus('unknown')}
                />
                <StatCard
                  label="Precisou da equipe"
                  value={summary.needs_team}
                  active={filters.status === 'needs_team'}
                  onClick={() => toggleStatus('needs_team')}
                />
                <StatCard label="Taxa de 👍" value={thumbsUpRate} />
                <StatCard label="Erros do modelo (período)" title="Só o período; os filtros não se aplicam" value={summary.model_errors} />
                <StatCard label="Custo (período)" title="Só o período; os filtros não se aplicam" compact value={formatCost(Number(summary.cost_usd))} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <BarList title="Temas" items={summary.topics.map((t) => ({ label: t.topic, count: t.count }))} />
                <BarList
                  title="Telas que mais geram dúvida"
                  items={summary.screens.map((s) => ({ label: s.screen, count: s.count }))}
                />
              </div>
            </div>
          )}

          <AdminHelpConversationList
            data={list}
            loading={listLoading}
            error={listError}
            activeId={openId}
            onRetry={reload}
            onOpen={openConversation}
            onPage={(next) => setQuery((prev) => ({ ...prev, page: next }))}
          />
        </>
      )}

      {openId && (
        <ConversationPanel onClose={closeConversation}>
          <AdminHelpConversation key={openId} conversationId={openId} onClose={closeConversation} onReplied={reload} />
        </ConversationPanel>
      )}
    </div>
  );
};

/** Painel lateral fixo à direita, sobre um fundo escurecido; fundo e Escape fecham. */
function ConversationPanel({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Não descarta um texto que está sendo escrito.
      if (e.target instanceof HTMLTextAreaElement && e.target.value.trim()) return;
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // z-[60]: acima do botão flutuante do chat de dúvidas (z-50), que o admin também vê.
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Conversa do chat de dúvidas"
        className="fixed inset-y-0 right-0 z-[60] w-full max-w-xl border-l border-border bg-background shadow-xl"
      >
        {children}
      </aside>
    </>
  );
}

function StatCard({
  label,
  value,
  active = false,
  compact = false,
  title,
  onClick,
}: {
  label: string;
  title?: string;
  value: string | number;
  active?: boolean;
  /** Valor em fonte menor, para textos longos como o custo. */
  compact?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-xs leading-tight text-muted-foreground">{label}</div>
      <div className={cn('mt-1 whitespace-nowrap font-bold tabular-nums text-foreground', compact ? 'text-xl' : 'text-2xl')}>
        {value}
      </div>
    </>
  );
  if (!onClick) return <div title={title} className="min-w-0 rounded-lg border border-border p-4">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={active ? 'Limpar o filtro' : 'Filtrar a lista'}
      className={cn(
        'min-w-0 rounded-lg border p-4 text-left transition-colors',
        active ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border hover:bg-muted/50'
      )}
    >
      {body}
    </button>
  );
}

function BarList({ title, items }: { title: string; items: { label: string; count: number }[] }) {
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="min-w-0 rounded-lg border border-border p-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">Sem dados no período</div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((item) => (
            <li key={item.label} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="truncate text-foreground" title={item.label}>
                  {item.label}
                </span>
                <span className="shrink-0 font-medium text-foreground">{item.count}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${(item.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AdminHelpTab;
